import React, { useState, useEffect } from 'react';
import { Cloud, Save, Zap, AlertCircle, CheckCircle2, Shield, ExternalLink, RefreshCw, KeyRound, Eye, EyeOff, Lock } from 'lucide-react';
import { api } from '../../../shared/api/api.js';
import { useNotificationStore } from '../../notifications/store/useNotificationStore.js';
import { SentryDiagnosticPanel } from './SentryDiagnosticPanel.js';

export const AdminSettingsView: React.FC = () => {
  const { showToast } = useNotificationStore();
  const [isLoading, setIsLoading] = useState(true);
  const [isTesting, setIsTesting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isEnvConfigured, setIsEnvConfigured] = useState(false);
  const [hasExistingSecret, setHasExistingSecret] = useState(false);
  const [showSecretKey, setShowSecretKey] = useState(false);
  
  const [formData, setFormData] = useState({
    accessKeyId: '',
    secretAccessKey: '',
    endpoint: '',
    bucketName: '',
    publicUrl: '',
  });

  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const data = await api.getR2Config();
        if (data.config) {
          setFormData({
            accessKeyId: data.config.accessKeyId || '',
            secretAccessKey: '', // Kept empty so admin can leave blank to retain existing saved secret
            endpoint: data.config.endpoint || '',
            bucketName: data.config.bucketName || '',
            publicUrl: data.config.publicUrl || '',
          });
        }
        setHasExistingSecret(Boolean(data.hasSecretKey));
        setIsEnvConfigured(data.isEnvConfigured);
      } catch (err) {
        console.error('Failed to fetch R2 config:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchConfig();
  }, []);

  const handleTestConnection = async () => {
    if (!formData.accessKeyId || (!formData.secretAccessKey && !hasExistingSecret) || !formData.endpoint || !formData.bucketName) {
      showToast('Data Tidak Lengkap', 'Harap isi semua bidang kredensial untuk pengujian.', 'error');
      return;
    }

    setIsTesting(true);
    setTestResult(null);
    try {
      const res = await api.testR2Config(formData);
      setTestResult({ success: true, message: res.message });
      showToast('Koneksi Berhasil', 'UIBN berhasil terhubung ke bucket Cloudflare R2 Anda.', 'success');
    } catch (err: any) {
      setTestResult({ success: false, message: err.message || 'Gagal terhubung ke R2.' });
      showToast('Koneksi Gagal', err.message || 'Pastikan kredensial dan endpoint R2 Anda sudah benar.', 'error');
    } finally {
      setIsTesting(false);
    }
  };

  const handleSaveConfig = async () => {
    if (!formData.accessKeyId || (!formData.secretAccessKey && !hasExistingSecret) || !formData.endpoint || !formData.bucketName) {
      showToast('Data Tidak Lengkap', 'Harap isi semua bidang kredensial untuk menyimpan.', 'error');
      return;
    }

    setIsSaving(true);
    try {
      const res = await api.saveR2Config(formData);
      setHasExistingSecret(true);
      setFormData(prev => ({ ...prev, secretAccessKey: '' })); // Reset input field to reveal the saved status
      showToast('Pengaturan Disimpan', 'Konfigurasi Cloudflare R2 dan Secret Access Key berhasil disimpan secara aman di pangkalan data.', 'success');
    } catch (err: any) {
      showToast('Gagal Menyimpan', err.message || 'Terjadi kesalahan saat menyimpan pengaturan.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-12 text-center bg-white rounded-2xl border border-slate-200">
        <RefreshCw className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
        <p className="text-xs text-slate-500">Memuat konfigurasi sistem...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
      {/* Banner Header */}
      <div className="bg-linear-to-r from-slate-900 to-indigo-950 text-white rounded-2xl p-6 sm:p-8 shadow-xl relative overflow-hidden">
        <div className="relative z-10 space-y-2">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-500/20 border border-indigo-400/30 text-indigo-300 text-xs font-semibold">
            <Cloud className="w-3.5 h-3.5 text-indigo-400" />
            <span>Integrasi Cloud Object Storage</span>
          </div>
          <h2 className="text-xl sm:text-2xl font-extrabold tracking-tight">Konfigurasi Cloudflare R2</h2>
          <p className="text-xs sm:text-sm text-slate-300 max-w-2xl leading-relaxed">
            Kelola penyimpanan berkas sampul buku secara terpusat di Cloudflare R2. Kredensial yang disimpan di database akan otomatis menimpa (override) variabel lingkungan (.env) secara aman.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Configuration Form */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="block text-xs font-bold text-slate-700 mb-1.5 uppercase tracking-wider">Endpoint API R2</label>
                <input
                  type="url"
                  value={formData.endpoint}
                  onChange={(e) => setFormData(prev => ({ ...prev, endpoint: e.target.value }))}
                  placeholder="https://<accountid>.r2.cloudflarestorage.com"
                  className="w-full px-4 py-2.5 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 outline-hidden transition"
                />
                <p className="text-[10px] text-slate-400 mt-1.5 flex items-center gap-1">
                  <ExternalLink className="w-3 h-3" />
                  <span>Dapatkan di Dashboard Cloudflare {'>'} R2 {'>'} Manage R2 API Tokens</span>
                </p>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5 uppercase tracking-wider">Access Key ID</label>
                <input
                  type="text"
                  value={formData.accessKeyId}
                  onChange={(e) => setFormData(prev => ({ ...prev, accessKeyId: e.target.value }))}
                  placeholder="Masukkan Access Key (32 karakter)"
                  className="w-full px-4 py-2.5 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 outline-hidden transition"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider">Secret Access Key</label>
                  {hasExistingSecret && !formData.secretAccessKey && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10px] font-bold">
                      <Lock className="w-2.5 h-2.5" />
                      <span>Tersimpan di Database</span>
                    </span>
                  )}
                  {formData.secretAccessKey && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-blue-50 border border-blue-200 text-blue-700 text-[10px] font-bold">
                      <KeyRound className="w-2.5 h-2.5" />
                      <span>Kunci Baru Diisi</span>
                    </span>
                  )}
                </div>
                <div className="relative">
                  <input
                    type={showSecretKey ? 'text' : 'password'}
                    value={formData.secretAccessKey}
                    onChange={(e) => setFormData(prev => ({ ...prev, secretAccessKey: e.target.value }))}
                    placeholder={hasExistingSecret ? '•••••••••••••••••••••••••••••••• (Tersimpan - Kosongkan jika tidak diubah)' : 'Masukkan Secret Access Key (64 karakter)'}
                    className="w-full pl-4 pr-10 py-2.5 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 outline-hidden transition"
                  />
                  {formData.secretAccessKey && (
                    <button
                      type="button"
                      onClick={() => setShowSecretKey(prev => !prev)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition p-0.5 cursor-pointer"
                      title={showSecretKey ? 'Sembunyikan Secret Key' : 'Tampilkan Secret Key'}
                    >
                      {showSecretKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  )}
                </div>
                <p className="text-[10px] text-slate-400 mt-1.5 leading-relaxed">
                  {hasExistingSecret
                    ? 'Secret Key Anda sudah tersimpan dengan aman di database. Anda tidak perlu memasukkannya kembali setiap kali mengubah endpoint atau bucket.'
                    : 'Kunci rahasia 64 karakter dari Cloudflare R2 API Token.'}
                </p>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5 uppercase tracking-wider">Nama Bucket</label>
                <input
                  type="text"
                  value={formData.bucketName}
                  onChange={(e) => setFormData(prev => ({ ...prev, bucketName: e.target.value }))}
                  placeholder="Contoh: iubn atau uibn-covers"
                  className="w-full px-4 py-2.5 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 outline-hidden transition"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5 uppercase tracking-wider">Public URL (Opsional / Kustom Domain)</label>
                <input
                  type="url"
                  value={formData.publicUrl}
                  onChange={(e) => setFormData(prev => ({ ...prev, publicUrl: e.target.value }))}
                  placeholder="https://cdn.domainanda.com (Kosongkan untuk auto-proxy internal)"
                  className="w-full px-4 py-2.5 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 outline-hidden transition"
                />
                <p className="text-[10px] text-slate-400 mt-1.5 leading-relaxed">
                  Jika dikosongkan, sistem UIBN akan otomatis menyajikan gambar melalui proxy CDN internal <code>/api/images/...</code> yang terbukti 100% andal dan bebas blokir ISP di Indonesia.
                </p>
              </div>
            </div>

            <div className="pt-4 flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={handleTestConnection}
                disabled={isTesting || isSaving}
                className="flex-1 px-5 py-3 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs flex items-center justify-center gap-2 transition cursor-pointer disabled:opacity-50"
              >
                {isTesting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4 text-amber-500" />}
                <span>Uji Koneksi R2</span>
              </button>
              <button
                type="button"
                onClick={handleSaveConfig}
                disabled={isTesting || isSaving}
                className="flex-1 px-5 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs flex items-center justify-center gap-2 transition cursor-pointer shadow-lg shadow-indigo-600/20 disabled:opacity-50"
              >
                {isSaving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                <span>Simpan Konfigurasi</span>
              </button>
            </div>

            {testResult && (
              <div className={`p-4 rounded-xl border flex items-start gap-3 animate-in fade-in slide-in-from-top-2 duration-200 ${
                testResult.success ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-rose-50 border-rose-200 text-rose-800'
              }`}>
                {testResult.success ? <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" /> : <AlertCircle className="w-5 h-5 text-rose-600 shrink-0" />}
                <p className="text-xs font-medium leading-relaxed">{testResult.message}</p>
              </div>
            )}
          </div>
        </div>

        {/* Info Sidebar */}
        <div className="space-y-6">
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 space-y-4">
            <div className="flex items-center gap-2 text-slate-800">
              <Shield className="w-4 h-4 text-indigo-600" />
              <h3 className="text-xs font-bold uppercase tracking-wider">Status Keamanan</h3>
            </div>
            
            <div className="space-y-3">
              <div className="p-3 rounded-xl bg-white border border-slate-200 flex items-center justify-between">
                <span className="text-[10px] font-bold text-slate-500">Env Config (.env)</span>
                {isEnvConfigured ? (
                  <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[9px] font-bold">Aktif</span>
                ) : (
                  <span className="px-2 py-0.5 rounded-full bg-slate-200 text-slate-500 text-[9px] font-bold">Kosong</span>
                )}
              </div>
              
              <div className="p-3 rounded-xl bg-white border border-slate-200 flex items-center justify-between">
                <span className="text-[10px] font-bold text-slate-500">Database Storage</span>
                {hasExistingSecret ? (
                  <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[9px] font-bold">Tersimpan & Aktif</span>
                ) : (
                  <span className="px-2 py-0.5 rounded-full bg-slate-200 text-slate-500 text-[9px] font-bold">Belum Dikonfigurasi</span>
                )}
              </div>
            </div>

            <div className="p-3.5 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-[10px] leading-relaxed">
              <div className="flex items-center gap-1.5 mb-1 text-amber-700 font-bold uppercase">
                <AlertCircle className="w-3.5 h-3.5" />
                <span>Peringatan Keamanan</span>
              </div>
              Kredensial disimpan dalam pangkalan data terenkripsi. Hindari penggunaan API Key dengan izin Full Account Access. Gunakan token dengan izin terbatas hanya untuk bucket spesifik (Object Read & Write) demi keamanan optimal sistem UIBN.
            </div>
          </div>

          <div className="bg-indigo-50 border border-indigo-100 rounded-2xl p-5 space-y-3">
            <h3 className="text-[10px] font-bold text-indigo-900 uppercase tracking-wider">Tentang Cloudflare R2</h3>
            <p className="text-[11px] text-indigo-700 leading-relaxed">
              Cloudflare R2 adalah penyimpanan objek tanpa biaya egress. UIBN menggunakan R2 untuk menyimpan ribuan file sampul buku dengan performa tinggi dan biaya efisien dibandingkan penyimpanan tradisional.
            </p>
            <a 
              href="https://dash.cloudflare.com/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[11px] font-bold text-indigo-600 hover:text-indigo-800 underline"
            >
              <span>Buka Console Cloudflare</span>
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      </div>

      {/* Sentry Telemetry & Dedicated Source Maps Diagnostic Panel */}
      <SentryDiagnosticPanel />
    </div>
  );
};
